import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { api, VENDOR_LABELS, VENDOR_COLORS } from "@/lib/api";
import RetailerCTA from "@/components/RetailerCTA";
import AffiliateNote from "@/components/AffiliateNote";
import { PriceDropBadge, Price } from "@/components/PriceDrop";
import CompleteTheLook from "@/components/CompleteTheLook";
import useRedirectFallback from "@/lib/useRedirectFallback";
import ProductCard from "@/components/ProductCard";
import Seo, { productSchema, breadcrumbSchema } from "@/components/Seo";
import EditOnSiteLink from "@/components/EditOnSiteLink";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  // When the product 404s, the redirect-fallback hook checks the admin
  // redirect map (legacy WordPress URLs etc.) and navigates away if a rule
  // matches — *before* we render our own "Product not found" panel.
  useRedirectFallback(Boolean(error));

  useEffect(() => {
    setProduct(null);
    setError("");
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        const cat = r.data.category_slugs?.[0];
        if (cat) {
          api
            .get("/products", { params: { category: cat, page_size: 8 } })
            .then((res) =>
              setRelated(res.data.items.filter((p) => p.slug !== slug).slice(0, 4))
            );
        }
      })
      .catch(() => setError("Product not found."));
  }, [slug]);

  const seoSchema = useMemo(() => {
    if (!product) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        productSchema(product),
        breadcrumbSchema([
          { name: "Home", url: `${window.location.origin}/` },
          ...(product.category_slugs?.[0]
            ? [
                {
                  name: product.category_slugs[0].replace(/-/g, " "),
                  url: `${window.location.origin}/category/${product.category_slugs[0]}`,
                },
              ]
            : []),
          { name: product.title, url: `${window.location.origin}/product/${product.slug}` },
        ]),
      ],
    };
  }, [product]);

  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="brut bg-white p-8 text-center" data-testid="product-not-found">
          <div className="font-display text-4xl mb-2">404</div>
          <p className="mb-4">{error}</p>
          <Link
            to="/"
            className="brut pcs-bg-pink text-white inline-block px-4 py-2 font-bold uppercase text-sm brut-press"
          >
            Back home
          </Link>
        </div>
      </div>
    );

  if (!product)
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 font-mono uppercase text-xs tracking-widest opacity-70">
        Loading...
      </div>
    );

  const v = VENDOR_LABELS[product.vendor] || product.vendor;
  const c = VENDOR_COLORS[product.vendor] || { bg: "#0A0A0A", text: "#fff" };

  return (
    <div data-testid={`product-page-${product.slug}`}>
      <Seo
        title={product.title}
        description={
          product.description ||
          `${product.title} — buy now from ${v}. $${product.price.toFixed(2)} via affiliate link.`
        }
        path={`/product/${product.slug}`}
        image={product.image_url}
        type="product"
        schema={seoSchema}
      />
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16 grid md:grid-cols-12 gap-8 lg:gap-12">
          {/* Image */}
          <div className="contents md:block md:col-span-7 md:order-1">
            <div className="brut-lg bg-white overflow-hidden order-1">
              <img
                src={product.image_url}
                alt={product.title}
                className="w-full aspect-square object-cover"
              />
            </div>
            {product.description && (
              <div
                className="order-3 md:mt-8 brut bg-white p-5 md:p-6 text-gray-800 leading-relaxed
                  [&_h1]:font-display [&_h1]:text-3xl [&_h1]:tracking-tight [&_h1]:mt-5 [&_h1]:mb-1
                  [&_h2]:font-display [&_h2]:text-2xl [&_h2]:tracking-tight [&_h2]:mt-5 [&_h2]:mb-1
                  [&_h3]:font-display [&_h3]:text-xl [&_h3]:tracking-tight [&_h3]:mt-4 [&_h3]:mb-1
                  [&_p]:my-2
                  [&_strong]:font-bold
                  [&_em]:italic
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ol]:space-y-1
                  [&_a]:underline [&_a]:pcs-text-pink hover:[&_a]:opacity-80
                  [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono
                  [&_table]:w-full [&_table]:border-2 [&_table]:border-black [&_table]:my-3
                  [&_th]:border-b-2 [&_th]:border-black [&_th]:bg-gray-100 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-mono [&_th]:uppercase [&_th]:text-xs [&_th]:tracking-widest
                  [&_td]:border-b [&_td]:border-black/20 [&_td]:px-2 [&_td]:py-1
                  [&_hr]:border-t-2 [&_hr]:border-black/30 [&_hr]:my-4
                  [&_blockquote]:border-l-4 [&_blockquote]:border-black [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:opacity-80 [&_blockquote]:my-3"
                data-testid="product-description"
              >
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                  {product.description}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="order-2 md:col-span-5 flex flex-col md:sticky md:top-36 md:self-start" data-testid="product-detail-panel">
            <div className="font-mono uppercase text-[11px] tracking-widest mb-3 flex flex-wrap gap-2 items-center">
              <Link to="/" className="hover:pcs-text-pink">Home</Link>
              <span className="opacity-50">/</span>
              {product.category_slugs?.[0] && (
                <>
                  <Link
                    to={`/category/${product.category_slugs[0]}`}
                    className="hover:pcs-text-pink"
                  >
                    {product.category_slugs[0].replace(/-/g, " ")}
                  </Link>
                  <span className="opacity-50">/</span>
                </>
              )}
              <span>{product.slug}</span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              {product.title}
            </h1>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span
                className="brut px-3 py-1.5 font-mono uppercase text-[11px] tracking-widest"
                style={{ backgroundColor: c.bg, color: c.text }}
                data-testid="vendor-badge"
              >
                Sold by {v}
              </span>
              {product.featured && (
                <span className="brut pcs-bg-orange px-3 py-1.5 font-mono uppercase text-[11px] tracking-widest">
                  Editor pick
                </span>
              )}
              <EditOnSiteLink type="product" slug={product.slug} />
            </div>

            <div className="mt-6 brut bg-white p-5">
              <div className="flex items-baseline justify-between">
                <div className="font-mono uppercase text-[11px] tracking-widest opacity-70">
                  Listed price
                </div>
                <Price product={product} className="font-display text-4xl" />
              </div>
              <PriceDropBadge product={product} className="mt-2" />
              {product.available === false ? (
                <div className="mt-4 brut pcs-bg-cream p-4 text-sm font-bold" data-testid="product-unavailable">
                  This listing is currently sold out or has ended on {v}. Check the matching picks below for similar items.
                </div>
              ) : (
                <div className="mt-4">
                  <RetailerCTA product={product} />
                </div>
              )}
              <p className="mt-3 font-mono uppercase text-[10px] tracking-widest opacity-50 text-center">
                Affiliate link · price &amp; availability set by {v}
              </p>
              <AffiliateNote className="mt-1 text-center" testId="product-affiliate-note" />
            </div>

            {product.tags?.length > 0 && (
              <div className="mt-6 flex items-center gap-2 flex-wrap">
                <Tag className="w-3.5 h-3.5 opacity-70" strokeWidth={2.5} />
                {product.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono uppercase text-[10px] tracking-widest opacity-70"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <CompleteTheLook slug={product.slug} />

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-none mb-6 md:mb-10">
            More like this.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
